import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Bell01, Grid01, Menu01, Star01 } from "@untitledui/icons";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

import MastercardLogo from "@/assets/images/MastercardLogo.svg";
import LanguageSwitcher from "@/Components/LanguageSwitcher";
import TimezoneBar from "@/Components/TimezoneBar";
import TourGuide from "@/Components/TourGuide";
import { useAuth } from "@/hooks/useAuth";
import TooltipButton from "@/shared/components/TooltipButton";
import { useAppSelector } from "@/store/hooks";
import {
  fetchUserDetailsByEmail,
  resetRoleState,
  selectFeatures,
  selectFeaturesLoading,
  selectFeaturesStatus,
} from "@/store/roleSlice";
import { initTheme } from "@/store/theme";

import NotificationsDropdown from "../Notifications/component/NotificationDropDown";
import ProfileMenu from "./components/ProfileMenu";
import type { NavbarProps } from "./types/navbar";

const cn = (...classes: (string | false | null | undefined)[]) => classes.filter(Boolean).join(" ");

const Navbar: React.FC<NavbarProps> = ({ drawerOpen, setDrawerOpen }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const features = useAppSelector(selectFeatures);
  const featuresLoading = useAppSelector(selectFeaturesLoading);
  const featureStatus = useAppSelector(selectFeaturesStatus);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch<any>(initTheme());
  }, [dispatch]);

  const { instance, accounts } = useMsal();
  const { account, login } = useAuth();
  const isAuthenticated = useIsAuthenticated();

  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement | null>(null);
  const [tourTrigger, setTourTrigger] = useState(0);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const displayName =
    accounts[0]?.name || account?.displayName || t("components.navBar.header.guest");

  const userPrincipalName = accounts[0]?.username || account?.userPrincipalName || "";

  const featurePaths = features
    .filter(
      (f): f is typeof f & { route_path: string } => Boolean(f.access) && Boolean(f.route_path)
    )
    .map((f) => f.route_path.toLowerCase());

  const hasAccess = (route: string) => featurePaths.includes(route.toLowerCase());

  const isItemActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const sidebarItems = [
    {
      label: t("components.navBar.sidebarItems.dashboard"),
      path: "/dashboard",
      route: "/dashboard",
      icon: <Grid01 className="h-[20px] w-[20px] flex-shrink-0" />,
    },
  ];

  return (
    <>
      {featureStatus === "failed" && (
        <div className="fixed inset-0 flex items-center justify-center z-[999] bg-[var(--bg-surface)]">
          <div className="flex flex-col items-center gap-3 text-red-500">
            <span className="text-lg font-medium">Failed to Load Widgets</span>
            <button
              onClick={() => {
                dispatch(resetRoleState());
                if (userPrincipalName) {
                  dispatch(fetchUserDetailsByEmail(userPrincipalName));
                }
              }}
              className="px-4 py-2 text-sm bg-red-100 text-red-600 rounded"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <header className="sticky top-0 z-[1000]">
        <div className="w-full border-b bg-[var(--nav-bg)] border-[var(--nav-border)]">
          <div className="mx-auto px-3 sm:px-4 lg:px-6">
            <div className="grid h-14 grid-cols-[auto_1fr_auto] items-center gap-3 text-[var(--text-primary)]">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-[var(--sidebar-hover)]"
                  onClick={() => setDrawerOpen((v) => !v)}
                >
                  <Menu01 className="h-5 w-5" />
                </button>

                <img
                  src={MastercardLogo}
                  alt="Mastercard Foundation"
                  className="h-8 w-auto cursor-pointer"
                  onClick={() => navigate("/", { replace: true })}
                />
              </div>

              <div className="relative mx-auto hidden min-w-0 items-center sm:flex">
                <TimezoneBar upn={userPrincipalName} />
              </div>

              <div className="flex flex-none items-center gap-4">
                <TooltipButton
                  position="bottom"
                  onClick={() => setTourTrigger((v) => v + 1)}
                  tooltip={t("components.navBar.tour", { defaultValue: "Take a Tour" })}
                  icon={<Star01 className="h-5 w-5" />}
                />
                <TourGuide userPrincipalName={userPrincipalName} flag={tourTrigger} />

                <div className="notification-icon relative" ref={notifRef}>
                  <TooltipButton
                    tooltip={t("components.navBar.notifications", {
                      defaultValue: "Notifications",
                    })}
                    onClick={() => setNotifOpen((v) => !v)}
                    position="bottom"
                    icon={<Bell01 className="h-5 w-5" />}
                  />

                  {notifOpen && (
                    <div
                      className="absolute right-0 mt-2 w-[360px] rounded-lg shadow-xl border z-[1000]"
                      style={{
                        background: "var(--bg-surface)",
                        borderColor: "var(--border-color)",
                      }}
                    >
                      <NotificationsDropdown />
                    </div>
                  )}
                </div>

                <div className="language-switcher relative">
                  <TooltipButton
                    tooltip={t("components.navBar.language", { defaultValue: "Languages" })}
                    position="bottom"
                    icon={<LanguageSwitcher upn={userPrincipalName} />}
                  />
                </div>

                <div className="profile-menu flex items-center gap-2">
                  <ProfileMenu
                    displayName={displayName}
                    email={userPrincipalName}
                    isAuthenticated={isAuthenticated}
                    onLogin={login}
                    onLogout={() => {
                      localStorage.setItem("msal-logout", Date.now().toString());
                      instance.setActiveAccount(null);
                      instance.logoutRedirect({
                        postLogoutRedirectUri: window.location.origin,
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <aside
        className={cn(
          "fixed left-0 top-14 h-[calc(100vh-56px)] border-r z-[900] transition-all duration-500",
          "bg-[var(--sidebar-bg)] border-[var(--border-color)]",
          drawerOpen ? "w-[300px]" : "w-[64px]"
        )}
      >
        <nav className="mt-2 px-2">
          <ul className="space-y-1">
            {!featuresLoading &&
              featureStatus === "succeeded" &&
              sidebarItems
                .filter((item) => hasAccess(item.route))
                .map((item) => {
                  const active = isItemActive(item.path);

                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={(e) => {
                          if (item.path === "/dashboard") {
                            e.preventDefault();
                            navigate("/", { replace: true });
                          }
                        }}
                        className={cn(
                          "flex items-center rounded-md text-sm font-medium transition-all relative",
                          drawerOpen
                            ? "gap-3 px-3 py-2"
                            : "w-full h-10 flex items-center justify-center",
                          drawerOpen
                            ? active
                              ? "bg-[var(--sidebar-active)] text-[var(--color-brand)]"
                              : "text-[var(--sidebar-text)] hover:bg-[var(--sidebar-hover)]"
                            : "text-[var(--sidebar-text)]"
                        )}
                      >
                        {drawerOpen && active && (
                          <span className="absolute left-0 top-1 bottom-1 w-1 bg-[var(--color-brand)] rounded-r-md" />
                        )}

                        <div className="flex items-center justify-center w-6 h-6 shrink-0 text-current">
                          {React.cloneElement(item.icon, { className: "w-5 h-5" })}
                        </div>

                        {drawerOpen && <span className="truncate">{item.label}</span>}
                      </Link>
                    </li>
                  );
                })}
          </ul>
        </nav>
      </aside>
    </>
  );
};

export default Navbar;
